import { Search, Settings, BarChart3, Rss, Layout, Megaphone, Zap, LogOut, Server, Filter, Brain } from "lucide-react";
import { NavLink } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

const mainItems = [
  { title: "Search Preview", url: "/", icon: Search },
  { title: "Analytics", url: "/admin/analytics", icon: BarChart3 },
];

const adminItems = [
  { title: "Feed Management", url: "/admin/feeds", icon: Rss },
  { title: "Layout Config", url: "/admin/layout", icon: Layout },
  { title: "Search Config", url: "/admin/search-config", icon: Settings },
  { title: "AI Search", url: "/admin/ai-search", icon: Brain },
  { title: "Categories & Filters", url: "/admin/categories-filters", icon: Filter },
  { title: "Marketing", url: "/admin/marketing", icon: Megaphone },
  { title: "System", url: "/admin/system", icon: Server },
];

interface AppSidebarProps {
  onLogout: () => void;
}

export function AppSidebar({ onLogout }: AppSidebarProps) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();

  return (
    <Sidebar collapsible="icon" className="border-r-0">
      <SidebarContent className="bg-sidebar pt-4">
        <div className="px-4 pb-4 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary flex-shrink-0">
            <Zap className="h-4 w-4 text-primary-foreground" />
          </div>
          {!collapsed && (
            <span className="text-lg font-bold tracking-tight text-sidebar-foreground">Botilia</span>
          )}
        </div>

        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/50 text-xs uppercase tracking-wider">
            {!collapsed && "Main"}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={location.pathname === item.url}>
                    <NavLink
                      to={item.url}
                      end
                      className={({ isActive }) =>
                        cn("hover:bg-sidebar-accent flex items-center gap-2 w-full", isActive && "bg-sidebar-accent text-sidebar-primary-foreground")
                      }
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/50 text-xs uppercase tracking-wider">
            {!collapsed && "Admin Panel"}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {adminItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={location.pathname === item.url}>
                    <NavLink
                      to={item.url}
                      end
                      className={({ isActive }) =>
                        cn("hover:bg-sidebar-accent flex items-center gap-2 w-full", isActive && "bg-sidebar-accent text-sidebar-primary-foreground")
                      }
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <button
                    onClick={onLogout}
                    className="hover:bg-sidebar-accent flex items-center gap-2 w-full text-sidebar-foreground/60 hover:text-sidebar-foreground"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Αποσύνδεση</span>
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
